import React, { useState, useEffect } from 'react';
import { BACKEND_URL } from '../config';
import { useParams, useNavigate } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import ReactMarkdown from 'react-markdown';

import {
  ArrowLeft,
  Clock,
  Folder
} from '@phosphor-icons/react';
const CATEGORY_NAMES = {
  'getting-started': 'Getting Started',
  'seats-billing': 'Seats & Billing',
  'agents': 'AI Agents',
  'workflows': 'Workflows',
  'integrations': 'Integrations',
  'troubleshooting': 'Troubleshooting'
};

const HelpArticle = () => {
  const { article_slug } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [article, setArticle] = useState(null);

  useEffect(() => {
    fetchArticle();
  }, [article_slug]);

  const fetchArticle = async () => {
    try {
      const response = await fetch(
        `${BACKEND_URL}/api/help/articles/${article_slug}`,
        { credentials: 'include' }
      );
      
      if (response.ok) {
        const data = await response.json();
        setArticle(data);
      } else {
        navigate('/help');
      }
    } catch (error) {
      console.error('Failed to fetch article:', error);
      navigate('/help');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto"></div>
          <p className="mt-4 text-gray-400">Loading article...</p>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] py-8 px-4">
        <div className="max-w-4xl mx-auto">
          <div className="glass rounded-2xl p-12 border border-[rgba(255,255,255,0.1)] text-center">
            <p className="text-gray-400 mb-4">Article not found</p>
            <button
              onClick={() => navigate('/help')}
              className="px-4 py-2 rounded-lg bg-blue-500/20 text-blue-400 hover:bg-blue-500/30 transition-colors"
            >
              Back to Help Centre
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>{article.title} - Help Centre - Man & Machine</title>
        <meta name="description" content={article.title} />
      </Helmet>

      <div className="min-h-screen bg-[#0A0A0A] pt-24 py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          {/* Navigation */}
          <div className="mb-6 flex items-center gap-4 flex-wrap">
            <button
              onClick={() => navigate('/help')}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.1)] transition-colors"
            >
              <ArrowLeft className="w-4 h-4" />
              Help Centre
            </button>
            <button
              onClick={() => navigate(`/help/categories/${article.category}`)}
              className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.1)] transition-colors"
            >
              <Folder className="w-4 h-4" />
              {CATEGORY_NAMES[article.category] || article.category}
            </button>
          </div>

          {/* Article Header */}
          <div className="mb-8">
            <h1 className="text-3xl sm:text-4xl font-bold text-white mb-4">{article.title}</h1>
            <div className="flex items-center gap-4 text-sm text-gray-400">
              <div className="flex items-center gap-1">
                <Folder className="w-4 h-4" />
                <span className="capitalize">{CATEGORY_NAMES[article.category] || article.category}</span>
              </div>
            </div>
          </div>

          {/* Article Content */}
          <div className="glass rounded-2xl p-6 sm:p-8 border border-[rgba(255,255,255,0.1)]">
              <div className="prose prose-invert max-w-none">
                <ReactMarkdown
                  components={{
                    h1: ({ children }) => <h1 className="text-2xl sm:text-3xl font-bold text-white mt-8 mb-4">{children}</h1>,
                    h2: ({ children }) => <h2 className="text-xl sm:text-2xl font-bold text-white mt-6 mb-3">{children}</h2>,
                    h3: ({ children }) => <h3 className="text-lg sm:text-xl font-semibold text-white mt-5 mb-2">{children}</h3>,
                    p: ({ children }) => <p className="text-gray-300 mb-4 leading-relaxed">{children}</p>,
                    ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-300">{children}</ul>,
                    ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-300">{children}</ol>,
                    li: ({ children }) => <li className="ml-4">{children}</li>,
                    strong: ({ children }) => <strong className="font-semibold text-white">{children}</strong>,
                    code: ({ inline, children }) => 
                      inline ? (
                        <code className="bg-[rgba(255,255,255,0.1)] text-blue-400 px-1.5 py-0.5 rounded text-sm font-mono">{children}</code>
                      ) : (
                        <code className="block bg-[rgba(255,255,255,0.05)] p-4 rounded text-sm font-mono overflow-x-auto mb-4 text-gray-300 border border-[rgba(255,255,255,0.1)]">{children}</code>
                      ),
                    blockquote: ({ children }) => (
                      <blockquote className="border-l-4 border-blue-500 pl-4 italic text-gray-400 my-4 bg-[rgba(255,255,255,0.05)] py-2">{children}</blockquote>
                    ),
                    a: ({ href, children }) => (
                      <a href={href} className="text-blue-400 hover:text-blue-300 underline" rel="noopener noreferrer">{children}</a>
                    )
                  }}
                >
                  {article.content}
                </ReactMarkdown>
              </div>
            </div>

          {/* Footer */}
          <div className="mt-8 text-center">
            <p className="text-gray-400 mb-4">Was this article helpful?</p>
            <div className="flex items-center justify-center gap-4">
              <button className="px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.1)] transition-colors">
                👍 Yes
              </button>
              <button className="px-4 py-2 rounded-lg bg-[rgba(255,255,255,0.05)] border border-[rgba(255,255,255,0.1)] text-gray-300 hover:bg-[rgba(255,255,255,0.1)] transition-colors">
                👎 No
              </button>
            </div>
            <div className="mt-6">
              <button
                onClick={() => navigate('/help')}
                className="text-blue-400 hover:text-blue-300 underline"
              >
                Back to Help Centre
              </button>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default HelpArticle;
